import { Grid, useTheme, Button, IconButton } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useContext, useState, useEffect, useRef } from 'react';
import FormInput from '../../SystemDesign/FormInput';
import FormSelect from '../../SystemDesign/FormSelect';
import MuiDialog from '../../SystemDesign/MuiDialog';
import PhoneInput from '../../SystemDesign/PhoneInput';
import * as yup from 'yup';
import { tokens } from '../../theme';
import { useCreateUserMutation, useGetRolesQuery } from '../../services/SkillsManagement';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddIcon from '@mui/icons-material/Add';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { PrimaryButton } from '../../SystemDesign/Styles';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { useDispatch } from 'react-redux';
import { ADD_EMPLOYEE } from '../../Reducers/Actions';
import { AuthContext } from '../../Context/AuthContext';
import ColoredSelector from '../../SystemDesign/ColoredSelector';

const AddEmployee = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [createUser] = useCreateUserMutation();
    const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
    const [close, setClose] = useState(false);
    const {user} = useContext(AuthContext);
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);
    const formikRef = useRef(null);
    const [roleOptions, setRoleOptions] = useState([]);

    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };
    
    // Fetch roles data from API
    const { data: rolesData, isLoading: isLoadingRoles, error: rolesError } = useGetRolesQuery({});

    const initialValues = {
        username: '',
        email: '',
        password: '',
        roleId: '',
        profile: {
            firstName: '',
            lastName: '',
            phone: '',
            birthday: '',
            gender: '',
            bio: ''
        }
    };

    const [formValues, setFormValues] = useState(initialValues);
    const submitPromiseRef = useRef(null);

    const genderOptions = [
        { id: 'Male', title: "Male" },
        { id: 'Female', title: "Female" }
    ];
    
    // Process roles data when it's available
    useEffect(() => {
        if (rolesData) {
            // Determine roles array using only the correct source
            const rolesArr = Array.isArray(rolesData)
                ? rolesData
                : (rolesData.roles || []);

            // Minimal mapping: use backend roleId and a readable title
            const options = rolesArr
                .map((role) => {
                    const idNum = Number(role?.roleId ?? role?.id);
                    if (!Number.isFinite(idNum)) return null;
                    return {
                        id: idNum,
                        title: role?.type,
                    };
                })
                .filter(Boolean);

            setRoleOptions(options);
        }
    }, [rolesData]);

    // Reset form values when dialog closes (to mimic the AddRole structure)
    useEffect(() => {
        if (close) {
            setFormValues(initialValues);
            setClose(false);
        }
    }, [close]);

    const formFields = [
        { type: 'text', name: 'username', label: "Username", required: true },
        { type: 'text', name: 'profile.firstName', label: "First Name", required: true },
        { type: 'text', name: 'profile.lastName', label: "Last Name", required: true },
        { type: 'email', name: 'email', label: "Email", required: true },
        { 
            type: 'custom', 
            name: 'password', 
            label: "Password", 
            required: true 
        },
        { type: 'phone', name: 'profile.phone', label: "Phone", required: false },
        { type: 'date', name: 'profile.birthday', label: "Birthday", required: false },
        { 
            type: 'select', 
            name: 'profile.gender', 
            label: "Gender", 
            options: genderOptions, 
            required: true 
        },
        { type: 'textarea', name: 'profile.bio', label: "Bio", rows: 4, required: false },
        { 
            type: 'select', 
            name: 'roleId', 
            label: "Role", 
            options: roleOptions, 
            required: true 
        },
    ];
    
    function getFieldGridProps(field) {
        switch (field.name) {
            case 'profile.firstName':
            case 'profile.lastName':
            case 'username':
                return { xs: 12, md: 4, lg: 4 };
            case 'profile.birthday':
            case 'profile.gender':
            case 'profile.phone':
                return { xs: 12, md: 4, lg: 4 };
            
            case 'email':
            case 'password':
                return { xs: 12, md: 4, lg: 6 };
           
            case 'roleId':
            case 'profile.bio':
                return { xs: 12, md: 12, lg: 12 };
            default:
                return { xs: 12, md: 6 };
        }
    }

    const formValidationSchema = yup.object().shape({
        username: yup.string()
            .required("Username is required")
            .min(3, "Username must be at least 3 characters"),
        email: yup.string()
            .email("Invalid email format")
            .required("Email is required"),
        password: yup.string()
            .required("Password is required")
            .min(6, "Password must be at least 6 characters"),
        roleId: yup.number()
            .required("Role is required"),
        
        profile: yup.object().shape({
            firstName: yup.string()
                .required("First name is required")
                .min(2, "First name must be at least 2 characters"),
            lastName: yup.string()
                .required("Last name is required")
                .min(2, "Last name must be at least 2 characters"),
            phone: yup.string()
                .nullable(),
            birthday: yup.date()
                .nullable()
                .transform((value, originalValue) => originalValue === '' ? null : value),
            gender: yup.string()
                .required("Gender is required")
                .oneOf(['Male', 'Female'], "Gender must be Male or Female"),
            bio: yup.string()
                .nullable()
        })
    });

    const handleChange = (e, formik) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            // Support nested fields like 'profile.firstName'
            formik.setFieldValue(name, value);
        } else if (name === 'roleId') {
            formik.setFieldValue(name, Number(value));
        } else {
            formik.setFieldValue(name, value);
        }
    };



    const handleSubmit = async (values, actions) => {
        try {
            // Create a FormData object to properly handle file uploads
            const formData = new FormData();
            
            // Add all fields directly to FormData
            formData.append('username', values.username);
            formData.append('email', values.email);
            formData.append('authPassword', values.password);
            
            // Convert roleId to number and append as string (FormData can only contain strings)
            formData.append('roleId', Number(values.roleId).toString());
            
            // Add profile fields
            formData.append('profile[firstName]', values.profile.firstName);
            formData.append('profile[lastName]', values.profile.lastName);
            
            if (values.profile.phone) {
                formData.append('profile[phone]', values.profile.phone);
            }
            
            if (values.profile.birthday) {
                formData.append('profile[birthday]', values.profile.birthday instanceof Date 
                    ? values.profile.birthday.toISOString() 
                    : values.profile.birthday);
            }
            
            formData.append('profile[gender]', values.profile.gender);
            
            if (values.profile.bio) {
                formData.append('profile[bio]', values.profile.bio);
            }
            
            // Build JSON payload (backend expects JSON with numeric roleId)
            const payload = {
                username: values.username,
                email: values.email,
                authPassword: values.password,
                roleId: Number(values.roleId),
                profile: {
                    firstName: values.profile.firstName,
                    lastName: values.profile.lastName,
                    phone: values.profile.phone || undefined,
                    birthday: values.profile.birthday
                        ? (values.profile.birthday instanceof Date
                            ? values.profile.birthday.toISOString()
                            : values.profile.birthday)
                        : undefined,
                    gender: values.profile.gender,
                    bio: values.profile.bio || undefined,
                },
            };

            const response = await createUser({ body: payload }).unwrap();
            
            // Show success message
            setInfoAlert({ 
                severity: 'success', 
                message: `Employee ${values.username} created successfully` 
            });
            
            dispatch({ type: ADD_EMPLOYEE, payload: response.user });
            actions.resetForm();
            setOpenSnackBar(true);
            if (submitPromiseRef.current) {
                submitPromiseRef.current(true);
                submitPromiseRef.current = null;
            }
        } catch (error) {
            console.error("Submit error:", error);
            setInfoAlert({ 
                severity: 'error', 
                message: error.data?.message || "Error creating employee" 
            });
            setOpenSnackBar(true);
            if (submitPromiseRef.current) {
                submitPromiseRef.current(false);
                submitPromiseRef.current = null;
            }
        }
        actions.setSubmitting(false);
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: "2rem" }}>
            <MuiDialog
                icon={<PersonAddIcon />}
                triggerButton={
                    <Button 
                     variant="contained"
            color="primary"
                        endIcon={<AddIcon />}
                    >
                        Add Employee
                    </Button>
                }
                title="Add New Employee"
                subtitle="Fill in the form to create a new employee"
                maxWidth="md"
                content={
                    <div>
                        <Formik
                            initialValues={formValues}
                            validationSchema={formValidationSchema}
                            validateOnChange={false}
                            validateOnBlur={false}
                            enableReinitialize={true}
                            onSubmit={handleSubmit}
                            innerRef={formikRef}
                        >
                            {(formik) => (
                                <Form id="createEmployeeForm" autoComplete="off">
                                    <Grid container spacing={2}>
                                        {formFields.map((field, index) => (
                                            <Grid item size={{...getFieldGridProps(field)}} key={index}>
                                                {field.type === 'text' || field.type === 'textarea' || field.type === 'email' || field.type === 'date' ? (
                                                    <FormInput
                                                        type={field.type}
                                                        name={field.name}
                                                        label={field.label}
                                                        required={field.required}
                                                        value={field.name.includes('.') 
                                                            ? formik.values.profile[field.name.split('.')[1]]
                                                            : formik.values[field.name]}
                                                        onChange={(e) => handleChange(e, formik)}
                                                        onBlur={formik.handleBlur}
                                                        error={field.name.includes('.') 
                                                            ? (formik.touched.profile && formik.touched.profile[field.name.split('.')[1]] && 
                                                                Boolean(formik.errors.profile && formik.errors.profile[field.name.split('.')[1]]))
                                                            : (formik.touched[field.name] && Boolean(formik.errors[field.name]))}
                                                        helperText={field.name.includes('.')
                                                            ? (formik.touched.profile && formik.touched.profile[field.name.split('.')[1]] && 
                                                                formik.errors.profile && formik.errors.profile[field.name.split('.')[1]])
                                                            : (formik.touched[field.name] && formik.errors[field.name])}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                ) : field.type === 'custom' && field.name === 'password' ? (
                                                    <FormInput
                                                        fullWidth
                                                        name="password"
                                                        label="Password"
                                                        required={true}
                                                        type={showPassword ? 'text' : 'password'}
                                                        placeholder="Password"
                                                        value={formik.values.password}
                                                        onChange={(e) => handleChange(e, formik)}
                                                        onBlur={formik.handleBlur}
                                                        error={formik.touched.password && Boolean(formik.errors.password)}
                                                        helperText={formik.touched.password && formik.errors.password}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <IconButton
                                                                    onClick={togglePasswordVisibility}
                                                                    edge="end"
                                                                    size="small"
                                                                    sx={{ color: colors.primary[500] }}
                                                                >
                                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            ),
                                                        }}
                                                    />
                                                ) : field.type === 'phone' ? (
                                                    <PhoneInput
                                                        name={field.name}
                                                        label={field.label}
                                                        required={field.required}
                                                        value={formik.values.profile.phone || ''}
                                                        onChange={(e) => handleChange(e, formik)}
                                                        onBlur={formik.handleBlur}
                                                        showFlags={false}
                                                        error={formik.touched.profile && formik.touched.profile.phone && 
                                                            Boolean(formik.errors.profile && formik.errors.profile.phone)}
                                                        helperText={formik.touched.profile && formik.touched.profile.phone && 
                                                            formik.errors.profile && formik.errors.profile.phone}
                                                    />
                                                ) : field.type === 'select' ? (
                                                    field.name === 'roleId' ? (
                                                        <ColoredSelector
                                                            options={roleOptions}
                                                            value={formik.values.roleId === '' ? null : formik.values.roleId}
                                                            onChange={(val) => {
                                                                const rawVal = (val && typeof val === 'object') ? (val.id ?? val.value) : val;
                                                                const num = Number(rawVal);
                                                                // Always store numeric value to satisfy validation schema and backend
                                                                formik.setFieldValue('roleId', Number.isFinite(num) ? num : '');
                                                            }}
                                                            placeholder="Select a role"
                                                            label="Role"
                                                            colorKey="color"
                                                            valueKey="id"
                                                            labelKey="title"
                                                            searchable={true}
                                                            required={true}
                                                            error={formik.touched.roleId && Boolean(formik.errors.roleId)}
                                                            helperText={formik.touched.roleId && formik.errors.roleId}
                                                            isLoading={isLoadingRoles}
                                                            showColorDot={false}
                                                        />
                                                    ) : (
                                                        <FormSelect
                                                            name={field.name}
                                                            label={field.label}
                                                            options={field.options}
                                                            required={field.required}
                                                            value={field.name.includes('.') 
                                                                ? formik.values.profile[field.name.split('.')[1]]
                                                                : formik.values[field.name]}
                                                            onChange={(e) => handleChange(e, formik)}
                                                            onBlur={formik.handleBlur}
                                                            error={field.name.includes('.') 
                                                                ? (formik.touched.profile && formik.touched.profile[field.name.split('.')[1]] && 
                                                                    Boolean(formik.errors.profile && formik.errors.profile[field.name.split('.')[1]]))
                                                                : (formik.touched[field.name] && Boolean(formik.errors[field.name]))}
                                                            helperText={field.name.includes('.')
                                                                ? (formik.touched.profile && formik.touched.profile[field.name.split('.')[1]] && 
                                                                    formik.errors.profile && formik.errors.profile[field.name.split('.')[1]])
                                                                : (formik.touched[field.name] && formik.errors[field.name])}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    )
                                                ) : null}
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    </div>
                }
                existAction={true}
                actionTitle="Create"
                actionFunction={async () => {
                    return new Promise((resolve) => {
                        submitPromiseRef.current = resolve;
                        formikRef.current?.submitForm?.();
                    });
                }}
                onClose={close}
            />
        </div>
    );
};

export default AddEmployee;